import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Mail, Lock, Loader2, Eye, EyeOff, ArrowLeft, KeyRound, ShieldCheck } from "lucide-react";
import { Helmet } from "react-helmet";
import { useAuth } from "@/hooks/useAuth";
import { useUserPreference } from "@/hooks/useUserPreference";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import ThemeToggle from "@/components/layout/ThemeToggle";

type ForgotPasswordStep = "email" | "otp" | "newPassword";

export default function Login() {
  const { isAuthenticated, isLoading, setUser } = useAuth();
  const [, navigate] = useLocation();
  const hasRedirected = useRef(false);
  const { toast } = useToast();

  // Get redirect URL from query params (set by PrivateRoute when auth is required)
  const explicitRedirect = new URLSearchParams(window.location.search).get("redirect");
  const redirectUrl = explicitRedirect || "/";

  // Server-only routes (handled by Express, not by the SPA router) need a
  // real browser navigation so the request actually hits the server. Without
  // this, wouter's pushState would just rewrite the URL and render a blank
  // SPA page — which is what was breaking the MCP OAuth handshake.
  const SERVER_ROUTE_PREFIXES = ["/oauth/", "/api/", "/.well-known/"];
  const isServerRoute = (path: string) =>
    SERVER_ROUTE_PREFIXES.some((p) => path.startsWith(p));
  const goAfterLogin = (path: string) => {
    if (isServerRoute(path)) {
      window.location.assign(path);
    } else {
      navigate(path);
    }
  };

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState<ForgotPasswordStep>("email");
  const [forgotEmail, setForgotEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [landingPage] = useUserPreference<string>("dashboard.landing_page", "/");

  useEffect(() => {
    if (!isLoading && isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      goAfterLogin(explicitRedirect || landingPage || "/");
    }
  }, [isAuthenticated, isLoading, landingPage]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiRequest('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (response && response.id) {
        setUser(response);
        toast({
          title: "Login successful",
          description: `Welcome back, ${response.name}!`,
        });
        // If no explicit redirect, check for landing page preference
        if (!explicitRedirect) {
          try {
            const prefRes = await fetch("/api/user-preferences/dashboard.landing_page", { credentials: "include" });
            if (prefRes.ok) {
              const pref = await prefRes.json();
              if (pref.value && typeof pref.value === "string") {
                goAfterLogin(pref.value);
                return;
              }
            }
          } catch {}
        }
        goAfterLogin(redirectUrl);
      }
    } catch (error: any) {
      toast({
        title: "Login failed",
        description: error.message || "Invalid email or password",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthLogin = (provider: 'google' | 'microsoft') => {
    // Pass redirect URL to OAuth flow so server can redirect back after login
    const params = redirectUrl && redirectUrl !== "/" ? `?redirect=${encodeURIComponent(redirectUrl)}` : "";
    window.location.href = `/api/auth/${provider}${params}`;
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      await apiRequest('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      toast({
        title: "OTP Sent",
        description: "If an account exists with this email, an OTP has been sent.",
      });
      setForgotStep("otp");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send OTP",
        variant: "destructive",
      });
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyOtp = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast({ title: "Invalid OTP", description: "Please enter the 6-digit OTP.", variant: "destructive" });
      return;
    }
    setForgotStep("newPassword");
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast({ title: "Weak password", description: "Password must be at least 6 characters.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast({ title: "Mismatch", description: "Passwords do not match.", variant: "destructive" });
      return;
    }
    setForgotLoading(true);
    try {
      await apiRequest('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail, otp, newPassword })
      });
      toast({
        title: "Password Reset Successful",
        description: "You can now sign in with your new password.",
      });
      // Reset state and go back to login
      resetForgotPassword();
    } catch (error: any) {
      toast({
        title: "Reset Failed",
        description: error.message || "Failed to reset password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotStep("email");
    setForgotEmail("");
    setOtp("");
    setNewPassword("");
    setConfirmNewPassword("");
    setShowNewPassword(false);
  };

  const renderForgotPasswordContent = () => {
    switch (forgotStep) {
      case "email":
        return (
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="Enter your registered email"
                  className="pl-10"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={forgotLoading}>
              {forgotLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending OTP...</>
              ) : (
                "Send OTP"
              )}
            </Button>
          </form>
        );

      case "otp":
        return (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              We've sent a 6-digit OTP to <strong>{forgotEmail}</strong>. Please check your inbox.
            </p>
            <div className="space-y-2">
              <Label htmlFor="otp">Enter OTP</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="otp"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  className="pl-10 text-center tracking-[0.3em] font-semibold text-lg"
                  value={otp}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(val);
                  }}
                  maxLength={6}
                  required
                  autoFocus
                />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={otp.length !== 6}>
              Verify OTP
            </Button>
            <button
              type="button"
              onClick={() => { setOtp(""); setForgotStep("email"); }}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Didn't receive the code? Resend
            </button>
          </form>
        );

      case "newPassword":
        return (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 mb-2">
              <ShieldCheck className="h-4 w-4" />
              <span>OTP verified. Set your new password.</span>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  className="pl-10 pr-10"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={6}
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-new-password">Confirm New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirm-new-password"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  className="pl-10"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  minLength={6}
                  required
                />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={forgotLoading}>
              {forgotLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Resetting...</>
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>
        );
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-sky-50 via-indigo-50 to-blue-50 dark:from-slate-950 dark:via-indigo-950 dark:to-blue-950 bg-fixed">
      <Helmet>
        <title>{showForgotPassword ? "Reset Password" : "Login"} | Dynamatix Intranet</title>
      </Helmet>

      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,rgba(255,255,255,0.5),rgba(255,255,255,0.8))] dark:[mask-image:linear-gradient(0deg,rgba(0,0,0,0.3),rgba(0,0,0,0.5))] -z-10"></div>

      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      <div className="flex items-center gap-3 mb-6 md:mb-10">
        <img src="/logo/singleLogo.png" alt="Dynamatix" className="h-10 w-10 md:h-12 md:w-12" />
        <div className="text-2xl md:text-3xl font-bold text-foreground">Dynamatix</div>
      </div>

      {showForgotPassword ? (
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center">
            <button
              type="button"
              onClick={resetForgotPassword}
              className="absolute left-6 top-6 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <CardTitle className="text-2xl font-bold text-foreground">Reset Password</CardTitle>
            <CardDescription>
              {forgotStep === "email" && "Enter your email to receive a reset OTP"}
              {forgotStep === "otp" && "Enter the OTP sent to your email"}
              {forgotStep === "newPassword" && "Create your new password"}
            </CardDescription>

            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 pt-3">
              {(["email", "otp", "newPassword"] as ForgotPasswordStep[]).map((step, i) => (
                <div key={step} className="flex items-center gap-2">
                  <div className={`h-2 w-8 rounded-full transition-colors ${
                    (["email", "otp", "newPassword"] as ForgotPasswordStep[]).indexOf(forgotStep) >= i
                      ? "bg-primary"
                      : "bg-muted"
                  }`} />
                </div>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {renderForgotPasswordContent()}
          </CardContent>
        </Card>
      ) : (
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-foreground">Sign in to Intranet</CardTitle>
            <CardDescription>Welcome back! Please sign in to continue</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* OAuth Buttons */}
            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => handleOAuthLogin('google')}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => handleOAuthLogin('microsoft')}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23">
                  <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                  <path fill="#f35325" d="M1 1h10v10H1z"/>
                  <path fill="#81bc06" d="M12 1h10v10H12z"/>
                  <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                  <path fill="#ffba08" d="M12 12h10v10H12z"/>
                </svg>
                Continue with Microsoft
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with email
                </span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <button
                    type="button"
                    onClick={() => {
                      setForgotEmail(email);
                      setShowForgotPassword(true);
                    }}
                    className="text-xs text-primary hover:underline font-medium"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    className="pl-10 pr-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
            </form>

            <div className="text-center text-sm text-muted-foreground">
              Don't have an account?{" "}
              <a href="/signup" className="text-primary hover:underline font-medium">
                Sign up
              </a>
            </div>
          </CardContent>
        </Card>
      )}

      <footer className="mt-10 text-center text-sm text-muted-foreground">
        <p>&copy; {new Date().getFullYear()} Dynamatix, Inc. All rights reserved.</p>
        <p className="mt-1">Secure enterprise intranet portal</p>
        <a href="/privacy-policy" className="mt-1 inline-block text-primary hover:underline">
          Privacy Policy
        </a>
      </footer>
    </div>
  );
}
